import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { MessageSquare, Check, X, Trash2, Star, Clock, Loader2, User, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'pending', label: 'Onay Bekleyen', color: 'text-orange-600 border-orange-600' },
  { id: 'approved', label: 'Onaylı', color: 'text-green-600 border-green-600' },
  { id: 'rejected', label: 'Reddedilen', color: 'text-red-600 border-red-600' },
  { id: 'all', label: 'Tümü', color: 'text-gray-700 border-gray-700' },
];

const AdminReviews = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState({ reviews: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    load();
  }, [status]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/reviews?status=${status}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.status === 401) { navigate('/admin/login'); return; }
      const d = await r.json();
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await fetch(`${API_URL}/api/admin/reviews/${id}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await load();
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const note = window.prompt('Ret sebebi (opsiyonel):', '');
    if (note === null) return; // cancelled
    setBusyId(id);
    try {
      await fetch(`${API_URL}/api/admin/reviews/${id}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ note })
      });
      await load();
    } finally { setBusyId(null); }
  };

  const del = async (id) => {
    if (!window.confirm('Bu yorumu tamamen silmek istediğinizden emin misiniz?')) return;
    setBusyId(id);
    try {
      await fetch(`${API_URL}/api/admin/reviews/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      await load();
    } finally { setBusyId(null); }
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return '-'; }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3 mb-6">
          <MessageSquare className="text-red-600" /> Yorumlar Yönetimi
        </h1>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6 overflow-x-auto">
          <div className="flex min-w-max border-b">
            {TABS.map(t => {
              const count = data.counts?.[t.id] ?? (t.id === 'all' ? data.counts?.total : 0);
              const isActive = status === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setStatus(t.id)}
                  data-testid={`review-tab-${t.id}`}
                  className={`px-5 py-3 font-medium text-sm whitespace-nowrap transition-colors border-b-2 ${
                    isActive ? t.color + ' bg-red-50/30' : 'text-gray-600 hover:text-red-600 border-transparent'
                  }`}
                >
                  {t.label} {count !== undefined && count !== null && <span className="ml-1 text-xs">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 size={24} className="animate-spin mr-2" /> Yükleniyor...
          </div>
        ) : data.reviews.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-16 text-center text-gray-500">
            <MessageSquare size={48} className="mx-auto mb-3 text-gray-300" />
            Bu bölümde yorum yok
          </div>
        ) : (
          <div className="space-y-4">
            {data.reviews.map(r => (
              <div key={r.id} className="bg-white rounded-lg shadow-sm p-5" data-testid={`review-card-${r.id}`}>
                <div className="flex items-start gap-4">
                  {/* Product */}
                  <div className="flex-shrink-0">
                    {r.product?.image && (
                      <img src={r.product.image} alt="" className="w-20 h-20 rounded-lg object-cover border" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono">#{r.product?.id || '?'}</span>
                      <span className="font-semibold text-sm text-gray-800 truncate">{r.product?.name || 'Ürün bulunamadı'}</span>
                    </div>
                    
                    {/* Rating + user */}
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <div className="flex items-center">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} size={16} className={i <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                        ))}
                        <span className="ml-1 text-sm font-semibold">{r.rating}/5</span>
                      </div>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <User size={12} /> {r.user_name}
                        <span className="text-gray-400">({r.user_email})</span>
                      </span>
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock size={12} /> {fmtDate(r.created_at)}
                      </span>
                      {r.order_id && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Package size={12} /> {r.order_id}
                        </span>
                      )}
                    </div>
                    
                    {/* Comment */}
                    <p className="text-gray-700 text-sm bg-gray-50 p-3 rounded border-l-4 border-gray-300 whitespace-pre-wrap break-words">
                      {r.comment}
                    </p>
                    
                    {/* Status badges */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {r.approved && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Check size={12} /> Onaylandı</span>}
                      {r.rejected && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><X size={12} /> Reddedildi</span>}
                      {!r.approved && !r.rejected && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-semibold flex items-center gap-1"><Clock size={12} /> Onay bekliyor</span>}
                      {r.admin_note && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Not: {r.admin_note}</span>}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {!r.approved && (
                      <button
                        onClick={() => approve(r.id)}
                        disabled={busyId === r.id}
                        data-testid={`approve-review-${r.id}`}
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                      >
                        <Check size={14} /> Onayla
                      </button>
                    )}
                    {!r.rejected && (
                      <button
                        onClick={() => reject(r.id)}
                        disabled={busyId === r.id}
                        data-testid={`reject-review-${r.id}`}
                        className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                      >
                        <X size={14} /> Reddet
                      </button>
                    )}
                    <button
                      onClick={() => del(r.id)}
                      disabled={busyId === r.id}
                      data-testid={`delete-review-${r.id}`}
                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
